<script lang="ts" setup>
  import constants from "~~/constants";

  const expand = ref(false);

  const {
    totalSubmissions,
    uploadedSubmissions,
    submissionUploadError,
    fileUploadError,
    uploadStatusCode,
    retry,
  } = await useUploadOfflineData();

  const showUploadProgress = computed(() => {
    return (
      totalSubmissions > 0 &&
      totalSubmissions !== uploadedSubmissions.value &&
      !submissionUploadError.value &&
      !fileUploadError.value
    );
  });
</script>

<template>
  <div
    class="fixed top-20 right-2 z-50 sm:right-12"
    v-if="showUploadProgress"
  >
    <div class="relative min-h-[48px] min-w-[48px]">
      <Transition name="fade-slide">
        <div
          v-if="expand"
          class="border-b-primary-500 absolute top-0 right-0 z-10 flex items-start gap-3 rounded-lg border border-b-4 border-gray-200 bg-white p-4 sm:min-w-xs"
        >
          <Icon
            name="heroicons-arrow-path"
            class="text-primary-500 shadow-lg"
          />
          <div class="flex-1 space-y-1">
            <div class="flex items-center justify-between gap-2">
              <p class="text-sm leading-0 font-semibold">
                {{ $t("shared.syncing-offline-submissions") }}
              </p>
              <UButton
                variant="outline"
                icon="heroicons-x-mark"
                class="border-none p-0 text-gray-500 ring-0 hover:text-gray-900"
                size="sm"
                @click="expand = false"
              />
            </div>
            <UProgress
              size="sm"
              status
              :model-value="uploadedSubmissions"
              :max="totalSubmissions"
            >
              <template #status>
                <p class="text-xs">
                  {{ uploadedSubmissions }} / {{ totalSubmissions }}
                </p>
              </template>
            </UProgress>
          </div>
        </div>
      </Transition>
      <Transition name="fade-slide">
        <div v-if="!expand" class="absolute top-0 right-0 z-20">
          <UTooltip
            :text="$t('shared.syncing-offline-submissions')"
            :delay-duration="100"
          >
            <UButton
              variant="none"
              class="flex size-8 animate-bounce items-center justify-center rounded-full border-none bg-white p-0 drop-shadow-lg"
              @click="expand = true"
            >
              <Icon
                name="heroicons-arrow-up-circle"
                size="20"
                class="text-blue-500"
              />
            </UButton>
          </UTooltip>
        </div>
      </Transition>
    </div>
  </div>

  <SubscriptionLimitReached
    v-if="uploadStatusCode == constants.STATUS_LIMIT_EXCEEDED"
  />
  <OfflineSubmissionUploadFailedModal
    v-else-if="submissionUploadError"
    @retry="retry"
  />
  <OfflineFilesUploadFailedModal
    v-else-if="fileUploadError"
    @retry="retry"
  />
</template>

<style scoped>
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition:
      opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: right top;
  }
  .fade-slide-enter-from,
  .fade-slide-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }
  .fade-slide-enter-to,
  .fade-slide-leave-from {
    opacity: 1;
    transform: scale(1);
  }
</style>
